import api from './api';

const authService = {
  /**
   * Register a new user
   */
  async register(email, password, firstName, lastName, inviteCode) {
    const response = await api.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
      inviteCode
    });

    // Under forced 2FA, registration returns an enrol stage (no token) — only
    // store a session if one is actually present (policy-off path).
    const data = response.data?.data || {};
    if (response.data.success && data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    return response.data;
  },

  /**
   * Login user (step 1: password). May return a full session, OR a two-factor
   * stage ('challenge' / 'enrol') that the Login UI must resolve before a
   * session is issued. Only stores the token when one is actually present.
   */
  async login(email, password) {
    const response = await api.post('/auth/login', {
      email,
      password
    });

    const data = response.data?.data || {};
    if (response.data.success && data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    return response.data;
  },

  /**
   * Item 89 Phase B — 2FA login steps. Each finalises a session on success.
   */
  _storeSession(data) {
    if (data?.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  },

  // Begin forced enrolment: returns { qrDataUrl, secret }.
  async start2faEnrol(enrolToken) {
    const response = await api.post('/auth/2fa/enrol/start', { enrolToken });
    return response.data;
  },

  // Confirm enrolment with the first code: returns { token, user, recoveryCodes }.
  async complete2faEnrol(enrolToken, code) {
    const response = await api.post('/auth/2fa/enrol/complete', { enrolToken, code });
    if (response.data.success) this._storeSession(response.data.data);
    return response.data;
  },

  // Second factor at login: pass { code } or { recoveryCode }.
  async verify2fa(challengeToken, { code, recoveryCode }) {
    const response = await api.post('/auth/2fa/challenge/verify', { challengeToken, code, recoveryCode });
    if (response.data.success) this._storeSession(response.data.data);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Item 89 — log out of ALL devices. The backend bumps the session epoch so
   * every outstanding token (including this one) is revoked; we then clear
   * local state exactly like a normal logout.
   */
  async logoutAllDevices() {
    try {
      await api.post('/auth/logout-all');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Get current user profile
   */
  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  /**
   * Get stored user
   */
  getStoredUser() {
    const user = localStorage.getItem('user');
    // A saved session that is not valid JSON must not take the app down: this
    // runs while the store is being built, so a throw here is a blank white
    // page on every load until the person clears their browser data
    // (20 Sep 2026 sign-up audit). Treat it as "no saved user" and tidy up.
    if (!user) return null;
    try {
      const parsed = JSON.parse(user);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      try { localStorage.removeItem('user'); } catch (__) { /* ignore */ }
      return null;
    }
  },

  /**
   * Get stored token
   */
  getToken() {
    return localStorage.getItem('token');
  }
};

export default authService;
